import { useEffect, useMemo, useState } from 'react';
import { AppShell } from './components/AppShell';
import type { ScreenKey } from './components/AppShell';
import { AddSheet } from './components/AddSheet';
import { Icon } from './ds';
import { HomeScreen } from './screens/HomeScreen';
import { LogScreen } from './screens/LogScreen';
import { PrepScreen } from './screens/PrepScreen';
import { GrowthScreen } from './screens/GrowthScreen';
import { AlbumScreen } from './screens/AlbumScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { SignInScreen } from './screens/SignInScreen';
import { SetupScreen } from './screens/SetupScreen';
import { useAuth } from './state/AuthContext';
import { useFamily } from './state/FamilyContext';

/**
 * Routing.
 *
 * Tab state is local — the five screens are peers in a bottom nav, not URLs
 * anyone links to. The one real route is `?share=CODE`, the link a grandparent
 * opens, which is read off the URL once at startup.
 */

function readShareCode(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('share');
}

export function App() {
  const { status } = useAuth();
  const { familyId, me, loading, error, children: kids, openSleeps } = useFamily();

  const [screen, setScreen] = useState<ScreenKey>('home');
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareCode] = useState(readShareCode);

  const babies = useMemo(() => {
    const babiesOnly = kids.filter((c) => c.kind === 'baby');
    // Fall back to every child, so a family that only registered a toddler can
    // still use the log rather than facing an empty picker.
    return babiesOnly.length ? babiesOnly : kids;
  }, [kids]);

  // A grandparent has nothing to do on the tracking tabs.
  const isViewer = me?.role === 'viewer';
  useEffect(() => {
    if (isViewer && screen !== 'album') setScreen('album');
  }, [isViewer, screen]);

  if (status === 'loading' || (status === 'signed-in' && loading)) {
    return <Splash />;
  }

  if (status === 'signed-out') {
    return <SignInScreen invitedBy={shareCode ? 'share' : null} />;
  }

  if (!familyId || !me) {
    return <SetupScreen shareCode={shareCode} />;
  }

  return (
    <>
      <AppShell
        screen={screen}
        onNavigate={setScreen}
        onAdd={isViewer || screen === 'album' ? undefined : () => setAddOpen(true)}
      >
        {error ? <ErrorBanner message={error} /> : null}

        {isViewer ? (
          <AlbumScreen readOnly />
        ) : screen === 'home' ? (
          <HomeScreen onNavigate={setScreen} onOpenSettings={() => setSettingsOpen(true)} />
        ) : screen === 'log' ? (
          <LogScreen />
        ) : screen === 'prep' ? (
          <PrepScreen />
        ) : screen === 'growth' ? (
          <GrowthScreen />
        ) : (
          <AlbumScreen onOpenShare={() => setSettingsOpen(true)} />
        )}
      </AppShell>

      {!isViewer ? (
        <AddSheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          familyId={familyId}
          me={me}
          babies={babies}
          openSleeps={openSleeps}
        />
      ) : null}

      {settingsOpen ? <SettingsScreen onClose={() => setSettingsOpen(false)} /> : null}
    </>
  );
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        color: 'var(--text-subtle)',
      }}
    >
      <span style={{ color: 'var(--brand)', animation: 'spin 1.1s linear infinite', display: 'inline-flex' }}>
        <Icon name="Loader" size={26} />
      </span>
      <span style={{ fontSize: 'var(--text-sm)' }}>よみこみ中…</span>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      className="cjk"
      style={{
        margin: '12px 18px 0',
        padding: '11px 13px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--danger-subtle)',
        color: 'var(--clay-700)',
        fontSize: 'var(--text-xs)',
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
      }}
    >
      <span style={{ flex: 'none', marginTop: 1 }}>
        <Icon name="AlertCircle" size={15} />
      </span>
      <span>
        データを読み込めませんでした。電波を確認してみてください。
        <br />
        <span style={{ opacity: 0.75 }}>{message}</span>
      </span>
    </div>
  );
}
